import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { conversationAPI, profileAPI, videoCallAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Inbox.css';

const Inbox = () => {
  const [conversations, setConversations] = useState([]);
  const [missedCalls, setMissedCalls] = useState([]);
  const [showMissedCalls, setShowMissedCalls] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMissedCalls, setLoadingMissedCalls] = useState(false);
  const [profile, setProfile] = useState(null);
  const { userId } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadConversations();
    loadMissedCalls();
    loadProfile();
    
    // Refresh conversations and missed calls every 10 seconds
    const interval = setInterval(() => {
      loadConversations();
      loadMissedCalls();
    }, 10000);
    
    return () => clearInterval(interval);
  }, []);

  // Refresh conversations when page becomes visible (user returns to tab/page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadConversations();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also refresh when window gains focus
    const handleFocus = () => {
      loadConversations();
    };
    
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const loadProfile = async () => {
    try {
      const response = await profileAPI.getMyProfile();
      setProfile(response.data);
    } catch (err) {
      console.error('Failed to load profile:', err);
    }
  };

  const loadConversations = async () => {
    try {
      const response = await conversationAPI.getConversations();
      setConversations(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load conversations', err);
      setLoading(false);
    }
  };

  const loadMissedCalls = async () => {
    try {
      setLoadingMissedCalls(true);
      const response = await videoCallAPI.getMissedCalls(0, 10);
      setMissedCalls(response.data.content || []);
    } catch (err) {
      console.error('Failed to load missed calls', err);
    } finally {
      setLoadingMissedCalls(false);
    }
  };

  const handleClearAllMissedCalls = async () => {
    if (!window.confirm('Clear all missed calls? This will permanently delete them from the database.')) {
      return;
    }
    
    try {
      await videoCallAPI.clearMissedCalls();
      setMissedCalls([]);
      setShowMissedCalls(false);
    } catch (err) {
      console.error('Failed to clear missed calls:', err);
      alert('Failed to clear missed calls');
    }
  };

  const handleCallBack = async (call) => {
    try {
      const response = await videoCallAPI.initiateCall(call.callerId);
      navigate(`/call/${response.data.id}`);
    } catch (err) {
      console.error('Failed to initiate call:', err);
      const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to initiate call';
      alert(errorMsg);
    }
  };

  const handleMessageCaller = (call) => {
    // Navigate to chat with the caller
    navigate(`/profile/${call.callerId}`);
  };

  const handleChatClick = (conversationId) => {
    navigate(`/chat/${conversationId}`);
  };

  // WhatsApp-style "last seen" text from a UTC timestamp.
  const getLastSeenText = (lastSeen) => {
    if (!lastSeen) return '';
    const iso = lastSeen.endsWith('Z') ? lastSeen : lastSeen + 'Z';
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);

    if (mins < 1) return 'last seen just now';
    if (mins < 60) return `last seen ${mins}m ago`;
    if (hours < 24) return `last seen ${hours}h ago`;
    if (days === 1) return 'last seen yesterday';
    if (days < 7) return `last seen ${days}d ago`;
    return `last seen ${then.toLocaleDateString()}`;
  };

  if (loading) {
    return (
      <div className="inbox-container">
        <div className="loading">Loading inbox...</div>
      </div>
    );
  }

  return (
    <div className="inbox-container">
      <div className="inbox-content">
        <div className="inbox-header">
          <div className="inbox-header-left">
            <h1>Inbox</h1>
            <p className="inbox-subtitle">Your private messages</p>
          </div>
          
        </div>

        {/* Missed Calls Notification Banner - Always visible when there are missed calls */}
        {missedCalls.length > 0 && (
          <div className="missed-calls-notification">
            <div className="notification-content">
              <div className="notification-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
              </div>
              <div className="notification-text">
                <h3>You have {missedCalls.length} missed {missedCalls.length === 1 ? 'call' : 'calls'}</h3>
                <p>Someone tried to reach you while you were away</p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  className="btn-clear-missed"
                  onClick={handleClearAllMissedCalls}
                  title="Clear all missed calls"
                >
                  Clear All
                </button>
                <button 
                  className="btn-expand-missed"
                  onClick={() => setShowMissedCalls(!showMissedCalls)}
                >
                  {showMissedCalls ? (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="18 15 12 9 6 15"/>
                      </svg>
                      Hide
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                      View All
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {showMissedCalls && (
              <div className="missed-calls-list">
                {missedCalls.map((call) => (
                  <div key={call.id} className="missed-call-card">
                    <div 
                      className="missed-call-avatar"
                      onClick={() => navigate(`/profile/${call.callerId}`)}
                      style={{ cursor: 'pointer' }}
                      title="View profile"
                    >
                      {call.callerPhotoUrl ? (
                        <img src={call.callerPhotoUrl} alt={call.callerName} loading="lazy" />
                      ) : (
                        <div className="avatar-placeholder">
                          {call.callerName?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    <div className="missed-call-info">
                      <h4>{call.callerName || 'Unknown'}</h4>
                      <p className="missed-call-time">
                        {new Date(call.initiatedAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="missed-call-actions">
                      <button 
                        className="btn-call-back"
                        onClick={() => handleCallBack(call)}
                        title="Call back"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                        Call Back
                      </button>
                      <button 
                        className="btn-message-caller"
                        onClick={() => handleMessageCaller(call)}
                        title="Send message"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                        Message
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {conversations.length === 0 ? (
          <div className="no-messages-state">
            <h3>No messages yet</h3>
            <p>Start browsing profiles and send a message to connect!</p>
            <Link to="/discover" className="btn btn-primary">
              Browse Profiles
            </Link>
          </div>
        ) : (
          <div className="conversations-list">
            {conversations.map((conversation) => (
              <div 
                key={conversation.id} 
                className="conversation-card"
              >
                <div 
                  className="conversation-avatar"
                  onClick={() => navigate(`/profile/${conversation.otherUserId}`)}
                  style={{ cursor: 'pointer' }}
                  title="View profile"
                >
                  {conversation.otherUserAvatar ? (
                    <img src={conversation.otherUserAvatar} alt={conversation.otherUserName} loading="lazy" />
                  ) : (
                    <div className="avatar-placeholder">
                      {conversation.otherUserName?.charAt(0) || '?'}
                    </div>
                  )}
                  {conversation.online && (
                    <div className="online-indicator-wrapper">
                      <span className="online-indicator"></span>
                    </div>
                  )}
                </div>
                <div className="conversation-info" onClick={() => handleChatClick(conversation.id)}>
                  <div className="conversation-header">
                    <div className="conversation-name-status">
                      <h3>{conversation.otherUserName || 'Unknown User'}</h3>
                      {conversation.online ? (
                        <span className="online-status-text">
                          <span className="online-dot"></span>
                          Online
                        </span>
                      ) : conversation.lastSeen ? (
                        <span className="last-seen-text">
                          {getLastSeenText(conversation.lastSeen)}
                        </span>
                      ) : null}
                    </div>
                    <span className="conversation-time">
                      {conversation.lastMessageTime ? new Date(conversation.lastMessageTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                  <p className="conversation-preview">
                    {conversation.lastMessage || 'No messages yet'}
                  </p>
                  {conversation.unreadCount > 0 && (
                    <span className="unread-badge">{conversation.unreadCount}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PHASE 1: Credits Purchase Modal disabled - all messaging is free */}
    </div>
  );
};

export default Inbox;
